// =============================================================================
// SchedulerPage.js — Scheduled task manager
// =============================================================================
// Displays all scheduled background tasks (from the Schedules DB table) and
// lets admins:
//   • Enable / disable a schedule (toggle the IsEnabled flag)
//   • Change the repeat interval in minutes (inline edit, saves on blur or Enter)
//   • Manually trigger a run immediately (Run Now)
//   • View run history for a specific schedule (expandable history panel)
//
// The page auto-refreshes every 15 seconds to pick up status changes from
// server/scheduler.js which actually executes the tasks.
//
// TO ADD A NEW SCHEDULED TASK:
//   1. Insert a row into the Schedules table (see db/migrations/008_schedules.sql)
//      with a unique TaskKey, e.g. 'pimberly-sync'.
//   2. Add a case for that TaskKey in server/scheduler.js :: executeTask()
//   3. The new schedule card will appear here automatically.
//
// API calls used: getSchedules, updateSchedule, runScheduleNow,
//                 getScheduleHistory, getRecentScheduleLogs
// =============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { getSchedules, updateSchedule, runScheduleNow, getScheduleHistory, getRecentScheduleLogs } from '../api';

export default function SchedulerPage() {
  const [schedules, setSchedules] = useState([]);    // all schedule rows from the DB
  const [recentLogs, setRecentLogs] = useState([]);  // last 30 log entries across all tasks
  const [historyFor, setHistoryFor] = useState(null); // ScheduleId whose history panel is open, or null
  const [history, setHistory] = useState([]);         // run-history rows for the open schedule
  const [loading, setLoading] = useState(true);       // initial load spinner
  const [running, setRunning] = useState(new Set());  // Set of ScheduleIds currently being triggered manually
  const [editingInterval, setEditingInterval] = useState({}); // { [ScheduleId]: currentInputValue }

  // refresh — loads both schedules list and recent logs in parallel.
  // Called on mount, every 15s, and after mutations.
  const refresh = useCallback(async () => {
    try {
      const [sRes, lRes] = await Promise.all([getSchedules(), getRecentScheduleLogs(30)]);
      setSchedules(sRes.data);
      setRecentLogs(lRes.data);
    } catch (err) {
      console.error('Failed to load schedules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh every 15 seconds — keeps status and next-run times current
  // without requiring a manual page reload
  useEffect(() => {
    const h = setInterval(refresh, 15000);
    return () => clearInterval(h); // cleanup on unmount
  }, [refresh]);

  // toggleEnabled — flips the IsEnabled flag for a schedule.
  // Disabled schedules are still stored but the scheduler loop skips them.
  const toggleEnabled = async (sched) => {
    try {
      await updateSchedule(sched.ScheduleId, { IsEnabled: !sched.IsEnabled });
      refresh();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.error || err.message));
    }
  };

  // saveInterval — persists an edited IntervalMinutes value.
  // Triggered on input blur or Enter key press.
  // Ignores saves where the value is empty, NaN, or < 1.
  const saveInterval = async (sched) => {
    const val = editingInterval[sched.ScheduleId];
    if (!val || isNaN(val) || val < 1) return;
    try {
      await updateSchedule(sched.ScheduleId, { IntervalMinutes: parseInt(val, 10) });
      // Remove the editing state so the input reverts to showing the saved DB value
      setEditingInterval(prev => { const n = {...prev}; delete n[sched.ScheduleId]; return n; });
      refresh();
    } catch (err) {
      alert('Failed to update interval: ' + (err.response?.data?.error || err.message));
    }
  };

  // triggerRun — tells the server to execute the task immediately (outside its normal interval).
  // The button shows "Running…" for 2s while we wait for the next refresh to show the result.
  const triggerRun = async (sched) => {
    setRunning(prev => new Set(prev).add(sched.ScheduleId));
    try {
      await runScheduleNow(sched.ScheduleId);
    } catch (err) {
      alert('Failed to trigger: ' + (err.response?.data?.error || err.message));
    }
    // Wait 2s then refresh to pick up the new LastStatus written by the server
    setTimeout(() => { refresh(); setRunning(prev => { const n = new Set(prev); n.delete(sched.ScheduleId); return n; }); }, 2000);
  };

  // showHistory — toggles the run-history panel for a schedule.
  // Clicking the same schedule again collapses the panel.
  const showHistory = async (sched) => {
    if (historyFor === sched.ScheduleId) { setHistoryFor(null); return; }
    setHistoryFor(sched.ScheduleId);
    try {
      const res = await getScheduleHistory(sched.ScheduleId, 20);
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  // fmtDate — formats an ISO timestamp to the browser's local date/time format
  const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';
  // fmtDuration — calculates human-readable run duration from start/end timestamps
  const fmtDuration = (start, end) => {
    if (!start || !end) return '—';
    const ms = new Date(end) - new Date(start);
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  if (loading) return <div style={styles.loading}>Loading schedules…</div>;

  return (
    <div style={styles.page}>
      {/* ---- Page header ---- */}
      <div style={styles.header}>
        <h1 style={styles.title}>Scheduler</h1>
        <p style={styles.subtitle}>Manage automated integration syncs</p>
      </div>

      {/* ---- Schedule cards grid ---- */}
      {/* Each card represents one row in the Schedules table.
          The left border colour is green (enabled) or grey (disabled). */}
      <div style={styles.grid}>
        {schedules.map(s => (
          <div key={s.ScheduleId} style={{...styles.card, borderLeft: `4px solid ${s.IsEnabled ? '#22c55e' : '#64748b'}`}}>
            {/* Card header: name, TaskKey, and enable/disable toggle */}
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>{s.Name}</div>
                {/* TaskKey is used in scheduler.js to identify which function to run */}
                <div style={styles.taskKey}>{s.TaskKey}</div>
              </div>
              <div style={styles.cardActions}>
                <button style={{...styles.toggleBtn, background: s.IsEnabled ? '#22c55e' : '#475569'}} onClick={() => toggleEnabled(s)}>
                  {s.IsEnabled ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>

            {/* Card body: interval editor, last-run info, next-run time */}
            <div style={styles.cardBody}>
              <div style={styles.row}>
                <span style={styles.label}>Interval:</span>
                <div style={{display:'flex',gap:6,alignItems:'center'}}>
                  {/* Inline interval editor — saves on blur or Enter */}
                  <input
                    type="number" min="1"
                    style={styles.intervalInput}
                    value={editingInterval[s.ScheduleId] ?? s.IntervalMinutes}
                    onChange={e => setEditingInterval(prev => ({...prev, [s.ScheduleId]: e.target.value}))}
                    onBlur={() => { if (editingInterval[s.ScheduleId] !== undefined) saveInterval(s); }}
                    onKeyDown={e => { if (e.key === 'Enter') saveInterval(s); }}
                  />
                  <span style={styles.label}>min</span>
                </div>
              </div>
              <div style={styles.row}>
                <span style={styles.label}>Last run:</span>
                <span style={styles.value}>{fmtDate(s.LastRunAt)}</span>
              </div>
              <div style={styles.row}>
                <span style={styles.label}>Status:</span>
                {/* Status badge: green = success, red = error, dark = never run / running */}
                <span style={{...styles.badge, background: s.LastStatus === 'success' ? '#166534' : s.LastStatus === 'error' ? '#991b1b' : '#374151'}}>
                  {s.LastStatus || 'never'}
                </span>
              </div>
              {/* Last error message — only shown if the previous run failed */}
              {s.LastError && <div style={styles.error}>{s.LastError}</div>}
              <div style={styles.row}>
                <span style={styles.label}>Next run:</span>
                <span style={styles.value}>{fmtDate(s.NextRunAt)}</span>
              </div>
            </div>

            {/* Card footer: Run Now button and History toggle */}
            <div style={styles.cardFooter}>
              <button style={styles.actionBtn} disabled={running.has(s.ScheduleId)} onClick={() => triggerRun(s)}>
                {running.has(s.ScheduleId) ? 'Running…' : '▶ Run Now'}
              </button>
              <button style={styles.actionBtn} onClick={() => showHistory(s)}>
                {historyFor === s.ScheduleId ? 'Hide History' : 'History'}
              </button>
            </div>

            {/* Expandable history panel — shows the last 20 run log entries */}
            {historyFor === s.ScheduleId && (
              <div style={styles.historyPanel}>
                {history.length === 0 ? <div style={{color:'#94a3b8',fontSize:13}}>No runs yet</div> :
                  <table style={styles.table}>
                    <thead><tr>
                      <th style={styles.th}>Started</th>
                      <th style={styles.th}>Duration</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Error</th>
                    </tr></thead>
                    <tbody>
                      {history.map(h => (
                        <tr key={h.LogId}>
                          <td style={styles.td}>{fmtDate(h.StartedAt)}</td>
                          <td style={styles.td}>{fmtDuration(h.StartedAt, h.FinishedAt)}</td>
                          <td style={styles.td}>
                            <span style={{...styles.badge, background: h.Status === 'success' ? '#166534' : h.Status === 'error' ? '#991b1b' : '#1e40af'}}>
                              {h.Status}
                            </span>
                          </td>
                          {/* ErrorMessage is truncated; full text shown in the title tooltip */}
                          <td style={{...styles.td, maxWidth:250, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={h.ErrorMessage}>{h.ErrorMessage || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ---- Recent activity log ---- */}
      {/* Shows the last 30 runs across ALL schedules (not just one).
          Loaded alongside schedules in the refresh() call. */}
      <div style={{marginTop:32}}>
        <h2 style={{color:'#e2e8f0',fontSize:18,marginBottom:12}}>Recent Activity</h2>
        {recentLogs.length === 0 ? <div style={{color:'#94a3b8'}}>No activity yet</div> :
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Task</th>
              <th style={styles.th}>Started</th>
              <th style={styles.th}>Duration</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Error</th>
            </tr></thead>
            <tbody>
              {recentLogs.map(l => (
                <tr key={l.LogId}>
                  <td style={styles.td}>{l.ScheduleName || l.TaskKey}</td>
                  <td style={styles.td}>{fmtDate(l.StartedAt)}</td>
                  <td style={styles.td}>{fmtDuration(l.StartedAt, l.FinishedAt)}</td>
                  <td style={styles.td}>
                    <span style={{...styles.badge, background: l.Status === 'success' ? '#166534' : l.Status === 'error' ? '#991b1b' : '#1e40af'}}>
                      {l.Status}
                    </span>
                  </td>
                  <td style={{...styles.td, maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={l.ErrorMessage}>{l.ErrorMessage || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
}

// =============================================================================
// styles — inline style objects used by this page.
// The scheduler uses inline styles (rather than CSS classes) because it is a
// standalone dark-themed panel that should not inherit the main app's light CSS.
// To convert to CSS classes, move each property into SchedulerPage.css and import it.
// =============================================================================
const styles = {
  page: { padding: '24px 32px', maxWidth: 1100, margin: '0 auto' },
  header: { marginBottom: 24 },
  title: { color: '#f1f5f9', fontSize: 28, fontWeight: 700, margin: 0 },
  subtitle: { color: '#94a3b8', fontSize: 14, marginTop: 4 },
  loading: { color: '#94a3b8', padding: 40, textAlign: 'center' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340, 1fr))', gap: 16 },
  card: { background: '#1e293b', borderRadius: 8, overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 16px 8px' },
  cardTitle: { color: '#f1f5f9', fontWeight: 600, fontSize: 16 },
  taskKey: { color: '#64748b', fontSize: 12, fontFamily: 'monospace', marginTop: 2 },
  cardActions: { display: 'flex', gap: 8 },
  toggleBtn: { border: 'none', color: '#fff', padding: '4px 14px', borderRadius: 12, fontWeight: 600, fontSize: 12, cursor: 'pointer' },
  cardBody: { padding: '4px 16px 12px' },
  row: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  label: { color: '#94a3b8', fontSize: 13, minWidth: 70 },
  value: { color: '#cbd5e1', fontSize: 13 },
  intervalInput: { width: 60, padding: '3px 6px', borderRadius: 4, border: '1px solid #475569', background: '#0f172a', color: '#e2e8f0', fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 10, color: '#fff', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
  error: { color: '#fca5a5', fontSize: 12, marginTop: 4, wordBreak: 'break-all' },
  cardFooter: { display: 'flex', gap: 8, padding: '8px 16px 12px', borderTop: '1px solid #334155' },
  actionBtn: { border: '1px solid #475569', background: 'transparent', color: '#94a3b8', padding: '5px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  historyPanel: { padding: '8px 16px 16px', borderTop: '1px solid #334155', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', color: '#94a3b8', padding: '6px 10px', borderBottom: '1px solid #334155', fontWeight: 600 },
  td: { color: '#cbd5e1', padding: '6px 10px', borderBottom: '1px solid #1e293b' },
};
